import React, {
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useParams } from "react-router-dom";
import MeaUpdateModal from "../../../../components/MeaUpdateModal";
import MeaStudentAvatar from "../../components/MeaStudentAvatar";
import slotChangeRequestsAPI from "../../../../services/slot_change_requests";
import slotsAPI from "../../../../services/slots";
import classesAPI from "../../../../services/classes";
import scheduleAPI from "../../../../services/schedule";
import {
  buildPreferredSlotOptions,
  combineDateAndTime,
  formatSlotLabel,
  requestClassId,
  requestLevelId,
  requestPreferences,
  slotsForClass,
  timeFromDate,
} from "../slotChangeUtils";
import { PERMISSION_TRAINING_STUDENTS } from "../../../../permissions";
import SlotPreferencePicker from "./SlotPreferencePicker";

const applyRequestState = (item, setters) => {
  setters.setPreferredSlotIds(
    requestPreferences(item)
      .map((pref) => pref.slot_id || pref.slot?.id)
      .filter(Boolean)
  );
  const nextDate = item?.requested_at ? new Date(item.requested_at) : new Date();
  setters.setRequestedDate(nextDate);
  setters.setRequestedTime(timeFromDate(nextDate));
  setters.setUrgency(item?.urgency || "NORMAL");
  setters.setReason(item?.reason || "");
};

const UpdateSlotChangeRequestModal = forwardRef((props, ref) => {
  const params = useParams();
  const schoolId = props.schoolId || params.schoolId;
  const trainingId = props.trainingId || params.trainingId;
  const accessToken = "";

  const innerRef = useRef();
  const [request, setRequest] = useState(props.request || null);
  const [preferredSlotIds, setPreferredSlotIds] = useState([]);
  const [requestedDate, setRequestedDate] = useState(null);
  const [requestedTime, setRequestedTime] = useState("00:00");
  const [urgency, setUrgency] = useState("NORMAL");
  const [reason, setReason] = useState("");

  const currentLevelId = requestLevelId(request);
  const currentClassId = requestClassId(request);
  const currentClassName =
    request?.from_class?.name || request?.student?.classe?.name || "";

  const slotsQuery = slotsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );
  const classesQuery = classesAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    {}
  );
  const scheduleQuery = scheduleAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    {}
  );

  const extraSlots = useMemo(
    () =>
      requestPreferences(request)
        .map((pref) => pref.slot)
        .filter(Boolean),
    [request]
  );

  const slotOptions = useMemo(
    () =>
      buildPreferredSlotOptions({
        classes: classesQuery?.data?.data,
        slots: slotsQuery?.data?.data,
        scheduleItems: scheduleQuery?.data?.data,
        currentClassId,
        levelId: currentLevelId,
        extraSlots,
      }),
    [
      classesQuery?.data?.data,
      slotsQuery?.data?.data,
      scheduleQuery?.data?.data,
      currentClassId,
      currentLevelId,
      extraSlots,
    ]
  );

  const currentSlots = useMemo(
    () =>
      slotsForClass({
        slots: slotsQuery?.data?.data,
        scheduleItems: scheduleQuery?.data?.data,
        classId: currentClassId,
      }),
    [slotsQuery?.data?.data, scheduleQuery?.data?.data, currentClassId]
  );

  useImperativeHandle(ref, () => ({
    show: (callback, item) => {
      const next = item || props.request || null;
      setRequest(next);
      applyRequestState(next, {
        setPreferredSlotIds,
        setRequestedDate,
        setRequestedTime,
        setUrgency,
        setReason,
      });
      innerRef.current.show(callback);
    },
  }));

  return (
    <MeaUpdateModal
      ref={innerRef}
      title="Modifier la demande"
      schoolId={schoolId}
      trainingId={trainingId}
      accessToken={accessToken}
      serviceAPI={slotChangeRequestsAPI}
      id={request?.id}
      dialogStyle={{ width: "50vw", maxHeight: "90vh" }}
      contentStyle={{ overflowY: "auto", maxHeight: "70vh" }}
      data={{
        preferred_slot_ids: preferredSlotIds,
        requested_at: combineDateAndTime(requestedDate, requestedTime),
        urgency,
        reason: reason || null,
      }}
    >
      <MeaStudentAvatar
        student={request?.student}
        schoolId={schoolId}
        trainingId={trainingId}
        permission={PERMISSION_TRAINING_STUDENTS.GET}
      />
      <small className="block text-600 mt-2 mb-2">
        {currentClassName && (
          <span className="block">
            Classe actuelle :{" "}
            <span className="font-medium text-900">{currentClassName}</span>
          </span>
        )}
        <span className="block">
          Créneau actuel :{" "}
          <span className="font-medium text-900">
            {request?.from_slot
              ? formatSlotLabel(request.from_slot)
              : currentSlots.length
                ? currentSlots.map((slot) => formatSlotLabel(slot)).join(", ")
                : "Aucun créneau"}
          </span>
        </span>
      </small>

      <label className="block text-900 font-medium mb-2 mt-3">
        Créneaux souhaités (par ordre de préférence)
      </label>
      <SlotPreferencePicker
        options={slotOptions}
        value={preferredSlotIds}
        onChange={setPreferredSlotIds}
        placeholder="Ajouter un créneau"
      />

      <div className="flex flex-column md:flex-row gap-3 mb-3">
        <div className="flex-1">
          <label className="block text-900 font-medium mb-2">Date de la demande</label>
          <Calendar
            value={requestedDate}
            onChange={(e) => setRequestedDate(e.value)}
            locale="fr"
            dateFormat="dd/mm/yy"
            showIcon
            showOnFocus={false}
            className="w-full"
          />
        </div>
        <div style={{ minWidth: "8rem" }}>
          <label className="block text-900 font-medium mb-2">Heure</label>
          <InputText
            type="time"
            value={requestedTime}
            onChange={(e) => setRequestedTime(e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      <label className="block text-900 font-medium mb-2">Urgence</label>
      <Dropdown
        value={urgency}
        onChange={(e) => setUrgency(e.value)}
        options={[
          { label: "Neutre", value: "NORMAL" },
          { label: "Prioritaire", value: "HIGH" },
          { label: "Urgent", value: "URGENT" },
        ]}
        appendTo={typeof document !== "undefined" ? document.body : undefined}
        className="w-full mb-3"
      />

      <label className="block text-900 font-medium mb-2">Motif</label>
      <InputTextarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        className="w-full mb-3"
      />
    </MeaUpdateModal>
  );
});

export default UpdateSlotChangeRequestModal;
